import type { AuthLoginRespVO } from './globals'
import type { ApiResponse } from './types'
import { createAlovaMockAdapter } from '@alova/mock'
import { createAlova } from 'alova'
import { createServerTokenAuthentication } from 'alova/client'
import adapterFetch from 'alova/fetch'
import VueHook from 'alova/vue'
import { decryptPayload, encryptPayload } from '../utils/crypto'
import mockData from './mock'
import { refreshToken as refreshTokenFn } from './modules/common'

const baseURL = import.meta.env.VITE_API_BASE_URL
const mockAdapter = createAlovaMockAdapter([mockData], {
  enable: false,
  httpAdapter: adapterFetch(),
  delay: 200,
  mockRequestLogger: false,
  onMockResponse: (data) => {
    return {
      response: new Response(JSON.stringify({ code: 0, data: data.body }), {
        status: 200,
        headers: data.responseHeaders
      }),
      headers: new Headers(data.responseHeaders)
    }
  }
})

const { onAuthRequired, onResponseRefreshToken } = createServerTokenAuthentication({
  refreshTokenOnSuccess: {
    isExpired: async (response) => {
      const contentType = response.headers.get('content-type') || ''
      if (!contentType.includes('application/json')) return false
      const result = decryptResponse<ApiResponse>(await response.clone().text())
      return result.code === 401
    },
    handler: async () => {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) throw new Error('刷新令牌不存在')
      const result = await refreshTokenFn({ refreshToken }).send(true) as AuthLoginRespVO
      localStorage.setItem('token', result.accessToken)
      localStorage.setItem('refreshToken', result.refreshToken)
    }
  },
  assignToken: (method) => {
    const token = localStorage.getItem('token')
    if (token) method.config.headers.Authorization = `Bearer ${token}`
  }
})

export const alovaInstance = createAlova({
  baseURL,
  statesHook: VueHook,
  requestAdapter: mockAdapter,
  cacheFor: null,
  beforeRequest: onAuthRequired((method) => {
    if (method.config.params) {
      method.config.params = { data: encryptPayload(method.config.params) }
    }
    if (method.data instanceof FormData) {
      const formData = new FormData()
      const fields: Record<string, unknown> = {}
      method.data.forEach((value, key) => {
        if (value instanceof Blob) formData.append(key, value)
        else fields[key] = value
      })
      formData.append('data', encryptPayload(fields))
      method.data = formData
    } else if (method.data !== undefined) {
      method.data = { data: encryptPayload(method.data) }
    }
    if (!localStorage.getItem('token') && method.meta?.authRole !== 'login') window.location.href = '/login'
  }),
  responded: onResponseRefreshToken({
    onSuccess: async (response: Response): Promise<any> => {
      const contentType = response.headers.get('content-type') || ''
      if (!contentType.includes('application/json')) return response.blob()

      const json = decryptResponse<ApiResponse>(await response.text())
      if (json.code === 0) return json.data
      if (json.code === 430) return clearAuthAndRedirect()

      const message = json.msg || '请求失败'
      ElMessage.error(message)
      throw new Error(message)
    },
    onError: (error) => {
      ElMessage.error(error.message || '服务器内部错误')
      throw error
    }
  })
})

function clearAuthAndRedirect() {
  localStorage.clear()
  window.location.href = '/login'
}

function decryptResponse<T>(text: string) {
  const response = JSON.parse(text) as { data?: string }
  return decryptPayload<T>(typeof response.data === 'string' ? response.data : text)
}
